import {t} from 'i18next';
import React, {useCallback} from 'react';
import ChecklistTile from '../../../../../incase-domain/components/checkbox/ChecklistTile';
import {getUpdatedAnswersForDebatableToggle} from './utils';
import {IQuestionElements} from '../../../../../incase-domain/elements/question';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';

interface DebatableOptionProps {
    highlightedElement: IQuestionElements;
    highlightedElementChangeHandler: {
        single: (field: keyof IQuestionElements, value: unknown) => void;
        multiple: (fields: (keyof IQuestionElements)[], values: unknown[]) => void;
    };
}

function hasAnswers(element: IQuestionElements): element is Extract<IQuestionElements, {answers: unknown}> {
    return 'answers' in element && Array.isArray(element.answers);
}

function DebatableOption({highlightedElement, highlightedElementChangeHandler}: DebatableOptionProps): JSX.Element {
    const {single, multiple} = highlightedElementChangeHandler;

    const handleDebatableChange = useCallback(
        (value: boolean) => {
            if (highlightedElement.type === ELEMENT.SEQUENCE_QUESTION || !hasAnswers(highlightedElement)) {
                single('isDebatable', value);
                return;
            }

            const updatedAnswers = getUpdatedAnswersForDebatableToggle(highlightedElement, value);

            if (highlightedElement.type === ELEMENT.MULTI_SELECT) {
                const codebookType = value ? 'debatable' : 'incorrect';
                multiple(['isDebatable', 'answers', 'defaultCodebookAnswerType'] as (keyof IQuestionElements)[], [
                    value,
                    updatedAnswers,
                    codebookType,
                ]);
                return;
            }

            multiple(['isDebatable', 'answers'] as (keyof IQuestionElements)[], [value, updatedAnswers]);
        },
        [highlightedElement, single, multiple]
    );

    return (
        <ChecklistTile
            value={highlightedElement.isDebatable ?? false}
            label={t('designSidepanel.debatableQuestion')}
            help={t('designSidepanel.debatableQuestionExpl')}
            onChange={handleDebatableChange}
        />
    );
}

export default DebatableOption;
